import type {
  PaymentMode,
  GroupPaymentStatus,
  SubmitPaymentRequest,
  SubmitPaymentResponse,
  PaymentErrorResponse
} from '../types/collectivePayments';

// URL base del backend
const API_BASE_URL = import.meta.env.VITE_API_URL || '';

class CollectivePaymentService {
  /**
   * Obtener todos los modos de pago disponibles
   */
  async getPaymentModes(): Promise<PaymentMode[]> {
    const url = `${API_BASE_URL}/api/collective-payments/modes`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Error al obtener modos de pago: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Obtener el estado de pagos de un grupo
   */
  async getGroupPaymentStatus(groupId: string): Promise<GroupPaymentStatus> {
    const url = `${API_BASE_URL}/api/collective-payments/groups/${groupId}/status`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('No se ha configurado el sistema de pagos colectivos para este grupo');
      }
      throw new Error(`Error al obtener estado de pagos: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Enviar un pago (transferencia o efectivo)
   */
  async submitPayment(
    groupId: string,
    paymentData: SubmitPaymentRequest
  ): Promise<SubmitPaymentResponse> {
    // Validación en cliente
    if (!paymentData.is_cash && !paymentData.receipt_url) {
      throw new Error('La URL del comprobante es requerida para pagos por transferencia');
    }

    if (paymentData.is_cash && !paymentData.organizer_signature_base64) {
      throw new Error('La firma es requerida para pagos en efectivo');
    }

    const url = `${API_BASE_URL}/api/collective-payments/groups/${groupId}/payments`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(paymentData)
    });

    if (!response.ok) {
      try {
        const error: PaymentErrorResponse = await response.json();
        throw new Error(error.detail || `Error al enviar pago: ${response.status}`);
      } catch (e) {
        throw new Error(`Error al enviar pago: ${response.status}`);
      }
    }

    return await response.json();
  }

  /**
   * Subir archivo a Supabase Storage
   * @param file Archivo a subir
   * @param groupId ID del grupo
   * @param supabaseClient Cliente de Supabase
   * @returns URL pública completa del archivo subido
   */
  async uploadReceiptFile(
    file: File,
    groupId: string,
    supabaseClient: any
  ): Promise<string> {
    const fileExt = file.name.split('.').pop();
    const fileName = `${groupId}/receipt_${Date.now()}.${fileExt}`;

    const { error } = await supabaseClient.storage
      .from('collective-payment-receipts')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw new Error('Error al subir el archivo. Por favor, inténtalo de nuevo.');
    }

    const { data } = supabaseClient.storage
      .from('collective-payment-receipts')
      .getPublicUrl(fileName);

    return data.publicUrl;
  }
}

export const collectivePaymentService = new CollectivePaymentService();

